import { c as _c } from "react/compiler-runtime";
// `<OgGlobe>` — an orthographic globe drawn as inline SVG for OG cards.
//
// Satori renders <svg>/<path>/<circle> natively (the same reason the wordmark
// is inlined, see chrome/og-logo.tsx), so this needs no WebGL, no canvas, and
// no network fetch — it is fully edge-safe and always renders. It mirrors the
// look of the in-app cobe globe: a dark sphere, real continent outlines, a
// faint graticule, a rim light, and a mode-tinted glow.
//
// The projection math + array building lives in og-globe-geometry.ts so this
// render path stays pure JSX.

import { buildGlobeGeometry } from './og-globe-geometry';
export interface OgGlobeProps {
  /** Globe diameter in pixels. */
  size: number;
  /** Continent + glow tint as an `rgb(r,g,b)` / hex string. */
  tint: string;
  /** Yaw in degrees (which longitude faces the viewer). Default -40 (Atlantic). */
  rotationDeg?: number;
  /** Tilt in degrees (positive lifts the north pole toward the viewer). Default 18. */
  tiltDeg?: number;
}
export function OgGlobe(props) {
  const $ = _c(45);
  const r = props.size / 2;
  let t0;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== props.rotationDeg || $[1] !== props.size || $[2] !== props.tiltDeg || $[3] !== props.tint || $[4] !== r) {
    const {
      land,
      graticule
    } = buildGlobeGeometry(props.size, props.rotationDeg ?? -40, props.tiltDeg ?? 18);
    t0 = props.size;
    t1 = props.size;
    t2 = `0 0 ${props.size} ${props.size}`;
    t3 = "http://www.w3.org/2000/svg";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        display: "flex"
      };
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t5 = "img";
    t6 = "Globe";
    let t12;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <radialGradient id="oggl-sphere" cx="38%" cy="32%" r="80%"><stop offset="0%" stopColor="#1c2138" /><stop offset="55%" stopColor="#0d1024" /><stop offset="100%" stopColor="#05060f" /></radialGradient>;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    if ($[19] !== props.tint) {
      t7 = <defs>{t12}<radialGradient id="oggl-glow" cx="50%" cy="50%" r="50%"><stop offset="62%" stopColor={props.tint} stopOpacity={0} /><stop offset="90%" stopColor={props.tint} stopOpacity={0.35} /><stop offset="100%" stopColor={props.tint} stopOpacity={0} /></radialGradient></defs>;
      $[19] = props.tint;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    if ($[21] !== r) {
      t8 = <circle cx={r} cy={r} r={r} fill="url(#oggl-glow)" />;
      $[21] = r;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    const t13 = r - 2;
    if ($[23] !== r || $[24] !== t13) {
      t9 = <circle cx={r} cy={r} r={t13} fill="url(#oggl-sphere)" />;
      $[23] = r;
      $[24] = t13;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t10 = graticule.map(_temp);
    let t14;
    if ($[26] !== props.tint) {
      t14 = pts_0 => <polyline key={`l${pts_0.slice(0, 14)}`} points={pts_0} fill="none" stroke={props.tint} strokeOpacity={0.92} strokeWidth={1.4} strokeLinejoin="round" strokeLinecap="round" />;
      $[26] = props.tint;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    t11 = land.map(t14);
    $[0] = props.rotationDeg;
    $[1] = props.size;
    $[2] = props.tiltDeg;
    $[3] = props.tint;
    $[4] = r;
    $[5] = t0;
    $[6] = t1;
    $[7] = t10;
    $[8] = t11;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t0 = $[5];
    t1 = $[6];
    t10 = $[7];
    t11 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  const t12 = r - 2;
  let t13;
  if ($[28] !== r || $[29] !== t12) {
    t13 = <circle cx={r} cy={r} r={t12} fill="none" stroke="#aeb6e8" strokeOpacity={0.28} strokeWidth={1.5} />;
    $[28] = r;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t0 || $[32] !== t1 || $[33] !== t10 || $[34] !== t11 || $[35] !== t13 || $[36] !== t2 || $[37] !== t3 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t14 = <svg width={t0} height={t1} viewBox={t2} xmlns={t3} style={t4} role={t5} aria-label={t6}>{t7}{t8}{t9}{t10}{t11}{t13}</svg>;
    $[31] = t0;
    $[32] = t1;
    $[33] = t10;
    $[34] = t11;
    $[35] = t13;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
}
function _temp(pts) {
  return <polyline key={`g${pts.slice(0, 14)}`} points={pts} fill="none" stroke="#5b6cff" strokeOpacity={0.12} strokeWidth={1} />;
}